<template>
  <div class="relative inline-block text-left"
      @keydown.up="focusPrev"
      @keydown.down="focusNext"
      @keydown.esc="close"
      @focusout="(event) => focusOut(event)"
      v-on-clickaway="close"
      >
    <div @keydown.enter.stop.prevent="toggle" @keydown.down="open">
      <slot
        name="trigger"
        v-bind="{isOpen: _isOpen, ...triggerProps}"
      />
    </div>
    <slot
      v-if="_isOpen"
      v-bind="{isOpen: _isOpen, close, activeIndex}"
    />
  </div>
</template>

<script>
import { directive as onClickaway } from 'vue3-click-away';
import Button from '../button/Button.vue';

export default {
  components: {
    [Button.name]: Button,
  },
  name: 'i-dropdown',
  directives: {
    onClickaway,
  },
  provide() {
    return {
      $dropdownContext: () => ({
        activeIndex: this.activeIndex,
        close: this.close,
      }),
    };
  },
  data() {
    return {
      activeIndex: -1,
      uncontrolledIsOpen: false,
    };
  },
  props: {
    isOpen: {
      type: Boolean,
      default: null,
    },
    itemCount: {
      type: Number,
      default: 0
    }
  },
  methods: {
    open() {
      if (this._isOpen) return;
      this._isOpen = true;
      this.activeIndex = -1;
    },
    close(e) {
      if (!this._isOpen) return;
      if (e) {
        e.stopPropagation();
        e.preventDefault();
      }
      this._isOpen = false;
    },
    toggle() {
      this.activeIndex = -1;
      this._isOpen = !this._isOpen;
    },
    focusNext() {
      if (this.itemCount === 0) return;

      // Start at 0 if nothing is focused yet
      if (this.activeIndex < 0) {
        this.activeIndex = 0;
        return;
      }

      // Clamp to maxIndex
      this.activeIndex = Math.min(this.activeIndex + 1, this.maxIndex);
    },
    focusPrev() {
      if (this.itemCount === 0) return;

      if (this.activeIndex < 0) {
        this.activeIndex = 0;
        return;
      }

      // Clamp to minimum
      this.activeIndex = Math.max(this.activeIndex - 1, 0);
    },
    focusOut(event) {
      // Close dropdown only if we tab out of the element and all its descendants
      if (!event.currentTarget.contains(event.relatedTarget)) {
        this.close();
      }
    },
  },
  computed: {
    triggerProps() {
      return {
        attrs: {
          'aria-haspopup': 'true',
          'aria-expanded': Boolean(this._isOpen).toString(),
          tabindex: 0,
        },
        toggle: this.toggle,
      };
    },
    /**
     * 1.) `controlled` (i.e. the consumer passes the `isOpen` prop)
     * 2.) `uncontrolled` (i.e. the dropdown manages its own local open/close state)
     */
    isControlled() {
      return typeof this.isOpen === 'boolean';
    },
    /**
     * update the open state
     * based on the current 'controlled' or 'uncontrolled' behavior
     */
    _isOpen: {
      get() {
        return this.isControlled ? this.isOpen : this.uncontrolledIsOpen;
      },
      set(value) {
        if (this.isControlled) {
          this.$emit('update:isOpen', value);
        } else {
          this.uncontrolledIsOpen = value;
        }
      },
    },
    maxIndex() {
      return Math.max(0, this.itemCount - 1);
    }
  },
  watch: {
    _isOpen: {
      immediate: true,
      handler(value) {
        // Note: emit open / close event on next tick
        // to ensure that the children components are mounted
        // since we are using v-if to render them
        this.$nextTick(() => {
          this.$emit(value ? 'open' : 'close', value);
        });
      },
    },
  },
};

</script>

<style>
  .relative {
    position: relative;
  }
</style>
